/**
 * 出口合规（Export Compliance）判读工具
 *
 * 两条链路的判读路径完全不同，不可混用：
 *   1) 预检链路  parsePrecheck(res)  —— res.data 即 ExportComplianceVerdict
 *   2) 业务链路  parseBusiness(res)  —— res.data.compliance 为 ComplianceInteractionResult
 *
 * 状态码语义：
 *   200    成功 / 预检软提示
 *   11000  命中合规（预检的 reviewSuggested/blocked；业务接口的 warnOnly/reviewSuggested/blocked）
 *   300900 预检/审查接口未捕获异常（failStrategy=CLOSE 时检测失败也走这里）
 */

export const EC_TYPE = {
  PASS: 'pass',
  WARN: 'warn',
  REVIEW: 'review',
  BLOCKED: 'blocked',
  DETECT_FAILED: 'detectFailed',
  ERROR: 'error'
};

const HIT_STATUS = '11000';
const OK_STATUS = '200';

/**
 * 预检链路判读：res.data 即 ExportComplianceVerdict
 */
export function parsePrecheck (res) {
  if (!res) return buildResult(EC_TYPE.ERROR, '', null);
  if (res.status === HIT_STATUS) {
    const verdict = res.data || {};
    // 命中合规：11000 且未标记 reviewSuggested/blocked 时按最严处理
    const type = verdict.reviewSuggested ? EC_TYPE.REVIEW : EC_TYPE.BLOCKED;
    return buildResult(type, pickMessage(verdict.message, res.err), verdict);
  }
  if (res.status === OK_STATUS) {
    const verdict = res.data;
    if (!verdict) return buildResult(EC_TYPE.PASS, '', null); // 无可校验内容，视为通过
    // 防御：reviewSuggested/blocked 按契约返回 11000，此处兜底以防后端异常返回 200 + 命中标记。
    // 阻断类标记优先于 detectFailed（阻断 > 放行），避免极端情况下误放行。
    if (verdict.blocked) return buildResult(EC_TYPE.BLOCKED, pickMessage(verdict.message, res.err), verdict);
    if (verdict.reviewSuggested) return buildResult(EC_TYPE.REVIEW, pickMessage(verdict.message, res.err), verdict);
    if (verdict.detectFailed) {
      return buildResult(EC_TYPE.DETECT_FAILED, pickMessage(verdict.message, res.err), verdict);
    }
    if (verdict.warnOnly) return buildResult(EC_TYPE.WARN, pickMessage(verdict.message, res.err), verdict);
    return buildResult(EC_TYPE.PASS, '', verdict);
  }
  return buildResult(EC_TYPE.ERROR, pickMessage(res.err), null);
}

/**
 * 业务链路判读：读 res.data.compliance（ComplianceInteractionResult）
 * 注意：正常成功时 compliance 可能为 null（如客户新增），属 pass
 */
export function parseBusiness (res) {
  if (!res) return buildResult(EC_TYPE.ERROR, '', null);
  if (res.status === HIT_STATUS) {
    const compliance = (res.data && res.data.compliance) || null;
    if (!compliance) {
      // 11000 命中但 compliance 缺失：拦截器已豁免 11000 全局提示，若仍按 ERROR 静默会导致「双重静默」。
      // 改判 BLOCKED，弹出合规窗并透传后端文案（err）。
      return buildResult(EC_TYPE.BLOCKED, pickMessage(res.err), null);
    }
    let type = EC_TYPE.BLOCKED;
    if (compliance.hardBlocked) type = EC_TYPE.BLOCKED;
    else if (compliance.needReview) type = EC_TYPE.REVIEW;
    else if (compliance.warnOnly) type = EC_TYPE.WARN;
    return buildResult(
      type,
      pickMessage(compliance.message, compliance.verdict && compliance.verdict.message, res.err),
      compliance.verdict || null,
      compliance
    );
  }
  if (res.status === OK_STATUS) {
    const compliance = (res.data && res.data.compliance) || null;
    if (!compliance) return buildResult(EC_TYPE.PASS, '', null);
    // 防御：命中合规按契约返回 11000，此处兜底以防后端异常返回 200 + 命中标记。
    // 阻断类标记优先于 detectFailed（阻断 > 放行）。
    if (compliance.hardBlocked) {
      return buildResult(EC_TYPE.BLOCKED, pickMessage(compliance.message, res.err), compliance.verdict || null, compliance);
    }
    if (compliance.needReview) {
      return buildResult(EC_TYPE.REVIEW, pickMessage(compliance.message, res.err), compliance.verdict || null, compliance);
    }
    if (compliance.warnOnly) {
      return buildResult(EC_TYPE.WARN, pickMessage(compliance.message, res.err), compliance.verdict || null, compliance);
    }
    if (compliance.detectFailed) {
      return buildResult(
        EC_TYPE.DETECT_FAILED,
        pickMessage(compliance.message, res.err),
        compliance.verdict || null,
        compliance
      );
    }
    return buildResult(EC_TYPE.PASS, '', compliance.verdict || null, compliance);
  }
  return buildResult(EC_TYPE.ERROR, pickMessage(res.err), null);
}

function buildResult (type, message, verdict, compliance) {
  return { type: type, message: message || '', verdict: verdict || null, compliance: compliance || null };
}

function pickMessage () {
  for (let i = 0; i < arguments.length; i++) {
    if (arguments[i]) return arguments[i];
  }
  return '';
}

/**
 * 取发起审查所需的检测凭证，优先 logId
 */
export function pickReviewEvidence (result) {
  const verdict = result && result.verdict;
  if (!verdict) return null;
  const engineLogId = verdict.logId || '';
  const detectionId = verdict.detectionId || '';
  if (!engineLogId && !detectionId) return null;
  return { engineLogId: engineLogId, detectionId: detectionId };
}
